/**
 * ComposerEngine — Reliable AI Music Generator
 * ─────────────────────────────────────────────
 * Self-contained music composition engine that generates STRUCTURALLY SOUND
 * music using Tone.js. Replaces unreliable MusicToolkit/MusicLearner patterns
 * with deterministic, structurally-aware composition.
 *
 * Features:
 *  - Structural progression (intro → build → drop → breakdown → outro)
 *  - Per-genre pattern generation (techno, house, ambient, drum&bass, dubstep, trance, hiphop, cinematic)
 *  - Telemetry callbacks for decisions and state changes
 *  - Auto-scaling visual intensity based on music state
 *  - Smooth BPM transitions and key/scale changes
 */

import * as Tone from 'tone';

/* ─────────────── Types ─────────────── */

export interface ComposerState {
  genre: string;
  bpm: number;
  key: string;
  scale: string;
  section: string;
  sectionBar: number;
  totalBars: number;
  confidence: number;
  energy: number;
}

export interface ComposerConfig {
  genre?: string;
  bpm?: number;
  key?: string;
  scale?: string;
}

/* ─────────────── Scale Intervals ─────────────── */

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const SCALE_INTERVALS: Record<string, number[]> = {
  major:           [0, 2, 4, 5, 7, 9, 11],
  minor:           [0, 2, 3, 5, 7, 8, 10],
  dorian:          [0, 2, 3, 5, 7, 9, 10],
  phrygian:        [0, 1, 3, 5, 7, 8, 10],
  lydian:          [0, 2, 4, 6, 7, 9, 11],
  mixolydian:      [0, 2, 4, 5, 7, 9, 10],
  harmonicMinor:   [0, 2, 3, 5, 7, 8, 11],
  pentatonicMinor: [0, 3, 5, 7, 10],
  blues:           [0, 3, 5, 6, 7, 10],
};

/* ─────────────── Section Definitions ─────────────── */

interface SectionDef {
  name: string;
  bars: number;
  energy: number;
  confidence: number;
}

const SECTIONS: SectionDef[] = [
  { name: 'intro',      bars: 8,  energy: 0.2, confidence: 0.6 },
  { name: 'build',      bars: 8,  energy: 0.5, confidence: 0.7 },
  { name: 'drop',       bars: 16, energy: 1.0, confidence: 0.9 },
  { name: 'breakdown',  bars: 8,  energy: 0.3, confidence: 0.8 },
  { name: 'outro',      bars: 4,  energy: 0.1, confidence: 0.5 },
];

const SECTION_CYCLE = ['intro', 'build', 'drop', 'breakdown', 'build', 'drop', 'outro'] as const;

/* ─────────────── Genre Defaults ─────────────── */

interface GenreProfile {
  bpm: number;
  energyPerSection: Record<string, number>;
}

const GENRE_PROFILES: Record<string, GenreProfile> = {
  techno:       { bpm: 135, energyPerSection: { intro: 0.15, build: 0.5, drop: 1.0, breakdown: 0.3, outro: 0.08 } },
  house:        { bpm: 128, energyPerSection: { intro: 0.2,  build: 0.5, drop: 1.0, breakdown: 0.3, outro: 0.1  } },
  ambient:      { bpm: 80,  energyPerSection: { intro: 0.1,  build: 0.3, drop: 0.6, breakdown: 0.2, outro: 0.05 } },
  'drum&bass':  { bpm: 170, energyPerSection: { intro: 0.2,  build: 0.6, drop: 1.0, breakdown: 0.4, outro: 0.1  } },
  drumnbass:    { bpm: 170, energyPerSection: { intro: 0.2,  build: 0.6, drop: 1.0, breakdown: 0.4, outro: 0.1  } },
  dubstep:      { bpm: 140, energyPerSection: { intro: 0.2,  build: 0.5, drop: 1.0, breakdown: 0.3, outro: 0.1  } },
  trance:       { bpm: 138, energyPerSection: { intro: 0.2,  build: 0.6, drop: 1.0, breakdown: 0.4, outro: 0.1  } },
  hiphop:       { bpm: 92,  energyPerSection: { intro: 0.2,  build: 0.5, drop: 0.9, breakdown: 0.3, outro: 0.1  } },
  cinematic:    { bpm: 100, energyPerSection: { intro: 0.1,  build: 0.5, drop: 0.9, breakdown: 0.2, outro: 0.05 } },
  loFi:         { bpm: 85,  energyPerSection: { intro: 0.15, build: 0.3, drop: 0.5, breakdown: 0.2, outro: 0.1  } },
  synthwave:    { bpm: 115, energyPerSection: { intro: 0.2,  build: 0.5, drop: 0.9, breakdown: 0.3, outro: 0.1  } },
  industrial:   { bpm: 130, energyPerSection: { intro: 0.2,  build: 0.6, drop: 1.0, breakdown: 0.3, outro: 0.1  } },
  experimental: { bpm: 120, energyPerSection: { intro: 0.1,  build: 0.4, drop: 0.8, breakdown: 0.2, outro: 0.05 } },
};

/* ─────────────── Note Helper ─────────────── */

function scaleNote(root: string, scale: string, degree: number, octave = 4): string {
  const intervals = SCALE_INTERVALS[scale] ?? SCALE_INTERVALS.minor;
  const rootIdx = NOTE_NAMES.indexOf(root.replace(/[0-9]/g, ''));
  if (rootIdx === -1) return `${root}${octave}`;
  const oct = octave + Math.floor(degree / intervals.length);
  const semitone = intervals[((degree % intervals.length) + intervals.length) % intervals.length];
  const noteIdx = (rootIdx + semitone) % 12;
  return `${NOTE_NAMES[noteIdx]}${oct}`;
}

/* ─────────────── ComposerEngine ─────────────── */

export class ComposerEngine {
  /* ── Public callbacks ── */
  onDecision: ((decision: string) => void) | null = null;
  onStateChange: ((state: ComposerState) => void) | null = null;

  /* ── Synths ── */
  private kick: Tone.MembraneSynth;
  private snare: Tone.NoiseSynth;
  private hihat: Tone.NoiseSynth;
  private bass: Tone.Synth;
  private lead: Tone.Synth;
  private pad: Tone.PolySynth;

  /* ── Routing ── */
  private masterGain: Tone.Gain;
  private bassFilter: Tone.Filter;
  private masterFilter: Tone.Filter;

  /* ── State ── */
  private _genre: string;
  private _bpm: number;
  private _key: string;
  private _scale: string;
  private _sectionIndex = 0;
  private _sectionBar = 0;
  private _sectionCycle: readonly string[];
  private _running = false;
  private _loopId: number | null = null;

  /* ── Pattern caches (rebuilt on genre/section change) ── */
  private _kickPattern: number[] = [];
  private _snarePattern: number[] = [];
  private _hihatPattern: number[] = [];
  private _bassPattern: { time: number; note: string; dur: number }[] = [];
  private _leadPattern: { time: number; note: string; dur: number }[] = [];
  private _padNotes: string[] = [];

  constructor(tone: typeof Tone, config?: ComposerConfig) {
    this._genre = config?.genre ?? 'techno';
    this._bpm = config?.bpm ?? GENRE_PROFILES[this._genre]?.bpm ?? 128;
    this._key = config?.key ?? 'C';
    this._scale = config?.scale ?? 'minor';
    this._sectionCycle = [...SECTION_CYCLE];

    /* ── Create synths ── */
    this.kick = new tone.MembraneSynth({
      pitchDecay: 0.02,
      octaves: 5,
      envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 0.3 },
    });

    this.snare = new tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.15, sustain: 0, release: 0.15 },
    });

    this.hihat = new tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.04, sustain: 0, release: 0.03 },
    });

    this.bass = new tone.Synth({
      oscillator: { type: 'sawtooth' },
      envelope: { attack: 0.01, decay: 0.3, sustain: 0.4, release: 0.3 },
      portamento: 0.02,
    });

    this.lead = new tone.Synth({
      oscillator: { type: 'sawtooth' },
      envelope: { attack: 0.005, decay: 0.2, sustain: 0.3, release: 0.4 },
      detune: 8,
      portamento: 0.05,
    });

    this.pad = new tone.PolySynth(tone.Synth, {
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.8, decay: 0.5, sustain: 0.6, release: 1.5 },
    });
    this.pad.maxPolyphony = 4;

    /* ── Filters & routing ── */
    this.bassFilter = new tone.Filter(400, 'lowpass');
    this.masterFilter = new tone.Filter(20000, 'lowpass');
    this.masterGain = new tone.Gain(0.8);

    // Kick → master
    this.kick.connect(this.masterGain);
    // Snare → master
    this.snare.connect(this.masterGain);
    // Hi-hat → master
    this.hihat.connect(this.masterGain);
    // Bass → low-pass → master
    this.bass.connect(this.bassFilter);
    this.bassFilter.connect(this.masterGain);
    // Lead → master
    this.lead.connect(this.masterGain);
    // Pad → master
    this.pad.connect(this.masterGain);

    // Master → filter → destination
    this.masterGain.connect(this.masterFilter);
    this.masterFilter.toDestination();

    console.log(`[Composer] Engine initialized — genre: ${this._genre}, key: ${this._key} ${this._scale}, bpm: ${this._bpm}`);
    this.onDecision?.(`Engine initialized — ${this._genre} in ${this._key} ${this._scale}`);

    this.buildPatterns();
  }

  /* ─────── Public API ─────── */

  /** Connect the composer's output to an external AudioNode (e.g. visualizer's audio input). */
  connectTo(destination: AudioNode): void {
    try {
      // Try direct connect first (works if same AudioContext)
      this.masterFilter.connect(destination as unknown as Tone.ToneAudioNode);
      console.log('[Composer] Connected to visualizer audio (direct)');
    } catch {
      // Cross-context bridge: Tone context → MediaStream → visualizer context
      try {
        const toneCtx = (this.masterFilter as unknown as { context: AudioContext }).context
          ?? Tone.getContext().rawContext;
        const dest = toneCtx.createMediaStreamDestination();
        (this.masterFilter as unknown as { connect: (n: unknown) => void }).connect(dest);
        const source = (destination.context as AudioContext).createMediaStreamSource(dest.stream);
        source.connect(destination);
        console.log('[Composer] Connected to visualizer audio (cross-context bridge)');
      } catch (e) {
        console.warn('[Composer] Failed to connect to visualizer:', e);
      }
    }
  }

  start(): void {
    if (this._running) return;
    this._running = true;
    this._sectionIndex = 0;
    this._sectionBar = 0;

    Tone.start();
    Tone.getTransport().bpm.value = this._bpm;
    Tone.getTransport().start();

    this.logDecision(`Starting composition — ${this._genre} in ${this._key} ${this._scale} @ ${this._bpm}BPM`);

    this.scheduleLoop();
    this.emitState();
  }

  stop(): void {
    this._running = false;
    if (this._loopId !== null) {
      Tone.getTransport().clear(this._loopId);
      this._loopId = null;
    }
    Tone.getTransport().stop();
    this.logDecision('Stopped — all sounds silenced');
    this.emitState();
  }

  setGenre(genre: string): void {
    const lower = genre.toLowerCase();
    if (!GENRE_PROFILES[lower]) {
      console.warn(`[Composer] Unknown genre "${genre}", falling back to techno`);
      this._genre = 'techno';
    } else {
      this._genre = lower;
    }
    const profile = GENRE_PROFILES[this._genre];
    if (profile) {
      // Smooth BPM transition to genre's default
      this.setBpm(profile.bpm);
    }
    this.logDecision(`Genre switched to ${this._genre}`);
    this.buildPatterns();
    this.emitState();
  }

  setBpm(bpm: number): void {
    const clamped = Math.max(60, Math.min(200, bpm));
    this._bpm = clamped;
    Tone.getTransport().bpm.rampTo(clamped, 2);
    this.logDecision(`BPM transition to ${clamped} (ramping over 2 seconds)`);
    this.emitState();
  }

  setKey(key: string, scale: string): void {
    this._key = key.toUpperCase().replace(/[0-9]/g, '');
    this._scale = scale.toLowerCase();
    this.logDecision(`Key/scale changed to ${this._key} ${this._scale}`);
    this.buildPatterns();
    this.emitState();
  }

  triggerDrop(): void {
    // Find the nearest 'drop' section ahead
    const dropIdx = this._sectionCycle.indexOf('drop', this._sectionIndex);
    if (dropIdx !== -1) {
      this._sectionIndex = dropIdx;
      this._sectionBar = 0;
      this.logDecision('Drop triggered! Full energy.');
      this.buildPatterns();
      this.emitState();
    } else {
      // Force-add drop at end
      this._sectionIndex = this._sectionCycle.length;
      (this._sectionCycle as string[]).push('drop');
      this._sectionBar = 0;
      this.logDecision('Drop triggered! (appended to cycle)');
      this.buildPatterns();
      this.emitState();
    }
  }

  triggerBuild(): void {
    const buildIdx = this._sectionCycle.indexOf('build', this._sectionIndex);
    if (buildIdx !== -1) {
      this._sectionIndex = buildIdx;
      this._sectionBar = 0;
      this.logDecision('Build forced!');
      this.buildPatterns();
      this.emitState();
    }
  }

  getState(): ComposerState {
    const sectionName = this.currentSectionName;
    let section: SectionDef = SECTIONS[0];
    for (let i = 0; i < SECTIONS.length; i++) {
      if (SECTIONS[i].name === sectionName) { section = SECTIONS[i]; break; }
    }
    const genreProfile = GENRE_PROFILES[this._genre];
    const baseEnergy = genreProfile?.energyPerSection[sectionName] ?? section.energy;

    return {
      genre: this._genre,
      bpm: this._bpm,
      key: this._key,
      scale: this._scale,
      section: sectionName,
      sectionBar: this._sectionBar,
      totalBars: this.totalBarsInSection,
      confidence: section.confidence,
      energy: baseEnergy * (this._sectionBar / Math.max(1, this.totalBarsInSection)) * 0.3 + baseEnergy * 0.7,
    };
  }

  /* ─────── Internal State ─────── */

  private get currentSectionName(): string {
    if (this._sectionIndex < this._sectionCycle.length) {
      return this._sectionCycle[this._sectionIndex];
    }
    return 'outro';
  }

  private get totalBarsInSection(): number {
    const name = this.currentSectionName;
    for (let i = 0; i < SECTIONS.length; i++) {
      if (SECTIONS[i].name === name) return SECTIONS[i].bars;
    }
    return 4;
  }

  /* ─────── Pattern Generation ─────── */

  private buildPatterns(): void {
    const sectionName = this.currentSectionName;

    switch (this._genre) {
      case 'techno':   this.buildTechnoPatterns(sectionName); break;
      case 'house':    this.buildHousePatterns(sectionName); break;
      case 'ambient':  this.buildAmbientPatterns(sectionName); break;
      case 'drum&bass':this.buildDrumBassPatterns(sectionName); break;
      case 'dubstep':  this.buildDubstepPatterns(sectionName); break;
      case 'trance':   this.buildTrancePatterns(sectionName); break;
      case 'hiphop':   this.buildHipHopPatterns(sectionName); break;
      case 'cinematic':this.buildCinematicPatterns(sectionName); break;
      default:         this.buildTechnoPatterns(sectionName); break;
    }

    this.logDecision(`Patterns generated for ${sectionName} — ${this._genre}`);
  }

  /* ── Techno ── */

  private buildTechnoPatterns(section: string): void {
    const bars = this.totalBarsInSection;
    this._kickPattern = [];
    this._snarePattern = [];
    this._hihatPattern = [];
    this._bassPattern = [];
    this._leadPattern = [];
    this._padNotes = [];

    if (section === 'intro' || section === 'outro') {
      // Intro/outro: filtered kick only, sparse
      const filterFreq = section === 'intro' ? 150 : 200;
      this.masterFilter.frequency.value = filterFreq;
      for (let bar = 0; bar < bars; bar++) {
        for (let beat = 0; beat < 4; beat++) {
          this._kickPattern.push(bar * 4 + beat);
        }
        // Sparse hi-hat
        this._hihatPattern.push(bar * 4 + 1);
        this._hihatPattern.push(bar * 4 + 3);
      }
    } else if (section === 'build') {
      this.masterFilter.frequency.value = 2000;
      for (let bar = 0; bar < bars; bar++) {
        for (let beat = 0; beat < 4; beat++) {
          this._kickPattern.push(bar * 4 + beat);
        }
        // Snare on 2 & 4
        this._snarePattern.push(bar * 4 + 1);
        this._snarePattern.push(bar * 4 + 3);
        // Off-beat hats, increasing density
        for (let beat = 0; beat < 4; beat++) {
          if (bar >= 4) {
            // 16th notes in second half of build
            this._hihatPattern.push(bar * 4 + beat);
            this._hihatPattern.push(bar * 4 + beat + 0.5);
          } else {
            this._hihatPattern.push(bar * 4 + beat + 0.5);
          }
        }
        // Bass
        this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 2), dur: 2 });
        this._bassPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 4, 2), dur: 1.5 });
      }
    } else if (section === 'drop') {
      this.masterFilter.frequency.value = 20000;
      for (let bar = 0; bar < bars; bar++) {
        // 4-on-the-floor kick
        for (let beat = 0; beat < 4; beat++) {
          this._kickPattern.push(bar * 4 + beat);
        }
        // Snare on 2 & 4 with ghost notes
        this._snarePattern.push(bar * 4 + 1);
        this._snarePattern.push(bar * 4 + 3);
        if (bar % 2 === 0) {
          this._snarePattern.push(bar * 4 + 1.5);
        }
        // Off-beat + 16th hi-hat
        for (let beat = 0; beat < 4; beat++) {
          this._hihatPattern.push(bar * 4 + beat + 0.5);
          this._hihatPattern.push(bar * 4 + beat + 0.75);
        }
        // Syncopated bass
        this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 1), dur: 1.5 });
        this._bassPattern.push({ time: bar * 4 + 1.5, note: scaleNote(this._key, this._scale, 7, 1), dur: 0.5 });
        this._bassPattern.push({ time: bar * 4 + 2.5, note: scaleNote(this._key, this._scale, 4, 1), dur: 1 });
        // Lead stab
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 5, 4), dur: 0.25 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 4, 4), dur: 0.25 });
      }
      // Pad chord
      this._padNotes = [
        scaleNote(this._key, this._scale, 0, 3),
        scaleNote(this._key, this._scale, 2, 3),
        scaleNote(this._key, this._scale, 4, 3),
      ];
    } else if (section === 'breakdown') {
      this.masterFilter.frequency.value = 600;
      // Sparse kick
      for (let bar = 0; bar < bars; bar++) {
        if (bar % 2 === 0) {
          this._kickPattern.push(bar * 4);
          this._kickPattern.push(bar * 4 + 2);
        }
      }
      // Pad wash
      this._padNotes = [
        scaleNote(this._key, this._scale, 0, 4),
        scaleNote(this._key, this._scale, 2, 4),
        scaleNote(this._key, this._scale, 4, 4),
        scaleNote(this._key, this._scale, 7, 4),
      ];
      // Melodic lead
      for (let bar = 0; bar < bars; bar++) {
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 5), dur: 1 });
        this._leadPattern.push({ time: bar * 4 + 1, note: scaleNote(this._key, this._scale, 2, 5), dur: 0.5 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 4, 5), dur: 1 });
        this._leadPattern.push({ time: bar * 4 + 3, note: scaleNote(this._key, this._scale, 3, 5), dur: 0.5 });
      }
    }
  }

  /* ── House ── */

  private buildHousePatterns(section: string): void {
    const bars = this.totalBarsInSection;
    this._kickPattern = [];
    this._snarePattern = [];
    this._hihatPattern = [];
    this._bassPattern = [];
    this._leadPattern = [];
    this._padNotes = [];

    if (section === 'intro' || section === 'outro') {
      this.masterFilter.frequency.value = section === 'intro' ? 200 : 300;
      for (let bar = 0; bar < bars; bar++) {
        for (let beat = 0; beat < 4; beat++) {
          this._kickPattern.push(bar * 4 + beat);
        }
      }
    } else if (section === 'build') {
      this.masterFilter.frequency.value = 2500;
      for (let bar = 0; bar < bars; bar++) {
        for (let beat = 0; beat < 4; beat++) {
          this._kickPattern.push(bar * 4 + beat);
        }
        this._snarePattern.push(bar * 4 + 1);
        this._snarePattern.push(bar * 4 + 3);
        for (let beat = 0; beat < 4; beat++) {
          this._hihatPattern.push(bar * 4 + beat + 0.5);
        }
        // Bass: root on 1, fifth on 3
        this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 2), dur: 2 });
        this._bassPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 4, 2), dur: 2 });
      }
    } else if (section === 'drop') {
      this.masterFilter.frequency.value = 20000;
      for (let bar = 0; bar < bars; bar++) {
        for (let beat = 0; beat < 4; beat++) {
          this._kickPattern.push(bar * 4 + beat);
        }
        this._snarePattern.push(bar * 4 + 1);
        this._snarePattern.push(bar * 4 + 3);
        for (let beat = 0; beat < 4; beat++) {
          this._hihatPattern.push(bar * 4 + beat + 0.5);
          this._hihatPattern.push(bar * 4 + beat + 0.75);
        }
        // Syncopated bass
        this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 1), dur: 1.5 });
        this._bassPattern.push({ time: bar * 4 + 1.5, note: scaleNote(this._key, this._scale, 9, 1), dur: 1 });
        this._bassPattern.push({ time: bar * 4 + 3, note: scaleNote(this._key, this._scale, 0, 1), dur: 1 });
        // Piano stabs
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 4), dur: 0.5 });
        this._leadPattern.push({ time: bar * 4 + 1, note: scaleNote(this._key, this._scale, 4, 4), dur: 0.25 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 5, 4), dur: 0.5 });
      }
      // Pad: major 7th-style chord
      this._padNotes = [
        scaleNote(this._key, this._scale, 0, 3),
        scaleNote(this._key, this._scale, 2, 3),
        scaleNote(this._key, this._scale, 4, 3),
        scaleNote(this._key, this._scale, 6, 3),
      ];
    } else if (section === 'breakdown') {
      this.masterFilter.frequency.value = 800;
      for (let bar = 0; bar < bars; bar++) {
        if (bar % 2 === 0) {
          this._kickPattern.push(bar * 4);
          this._kickPattern.push(bar * 4 + 2);
        }
        this._hihatPattern.push(bar * 4 + 0.5);
      }
      this._padNotes = [
        scaleNote(this._key, this._scale, 0, 4),
        scaleNote(this._key, this._scale, 2, 4),
        scaleNote(this._key, this._scale, 4, 4),
      ];
      for (let bar = 0; bar < bars; bar++) {
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 5, 4), dur: 1.5 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 4, 4), dur: 1 });
      }
    }
  }

  /* ── Ambient ── */

  private buildAmbientPatterns(section: string): void {
    const bars = this.totalBarsInSection;
    this._kickPattern = [];
    this._snarePattern = [];
    this._hihatPattern = [];
    this._bassPattern = [];
    this._leadPattern = [];
    this._padNotes = [];

    this.masterFilter.frequency.value = 3000;

    // Sparse kick on downbeats
    for (let bar = 0; bar < bars; bar++) {
      if (bar % 2 === 0) {
        this._kickPattern.push(bar * 4);
      }
    }

    // Slowly evolving pad
    this._padNotes = [
      scaleNote(this._key, this._scale, 0, 3),
      scaleNote(this._key, this._scale, 4, 3),
      scaleNote(this._key, this._scale, 7, 3),
    ];

    // Gentle bass swells
    for (let bar = 0; bar < bars; bar++) {
      this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 2), dur: 3.5 });
    }

    // Sparse lead melody
    if (section === 'drop' || section === 'breakdown') {
      for (let bar = 0; bar < bars; bar++) {
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 5, 4), dur: 2 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 7, 4), dur: 2 });
      }
    }
  }

  /* ── Drum & Bass ── */

  private buildDrumBassPatterns(section: string): void {
    const bars = this.totalBarsInSection;
    this._kickPattern = [];
    this._snarePattern = [];
    this._hihatPattern = [];
    this._bassPattern = [];
    this._leadPattern = [];
    this._padNotes = [];

    if (section === 'intro' || section === 'outro') {
      this.masterFilter.frequency.value = section === 'intro' ? 150 : 200;
      for (let bar = 0; bar < bars; bar++) {
        this._kickPattern.push(bar * 4);
        this._kickPattern.push(bar * 4 + 2);
        this._snarePattern.push(bar * 4 + 1);
        this._snarePattern.push(bar * 4 + 3);
      }
    } else {
      this.masterFilter.frequency.value = 20000;
      for (let bar = 0; bar < bars; bar++) {
        // Kick on 1 and 3 (half-time feel)
        this._kickPattern.push(bar * 4);
        this._kickPattern.push(bar * 4 + 2);
        // Snare on 2 and 4
        this._snarePattern.push(bar * 4 + 1);
        this._snarePattern.push(bar * 4 + 3);
        // 16th-note hi-hats (amen style)
        for (let sixteenth = 0; sixteenth < 16; sixteenth++) {
          this._hihatPattern.push(bar * 4 + sixteenth * 0.25);
        }
        // Rolling bass
        this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 2), dur: 1 });
        this._bassPattern.push({ time: bar * 4 + 1, note: scaleNote(this._key, this._scale, 0, 2), dur: 0.75 });
        this._bassPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 3, 2), dur: 0.5 });
        this._bassPattern.push({ time: bar * 4 + 3, note: scaleNote(this._key, this._scale, 5, 2), dur: 0.75 });
      }
      // Lead stabs
      for (let bar = 0; bar < bars; bar += 2) {
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 5, 4), dur: 0.5 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 7, 4), dur: 0.5 });
      }
    }
  }

  /* ── Dubstep ── */

  private buildDubstepPatterns(section: string): void {
    const bars = this.totalBarsInSection;
    this._kickPattern = [];
    this._snarePattern = [];
    this._hihatPattern = [];
    this._bassPattern = [];
    this._leadPattern = [];
    this._padNotes = [];

    if (section === 'intro' || section === 'outro') {
      this.masterFilter.frequency.value = section === 'intro' ? 150 : 200;
      for (let bar = 0; bar < bars; bar++) {
        this._kickPattern.push(bar * 4);
      }
    } else {
      this.masterFilter.frequency.value = 20000;
      for (let bar = 0; bar < bars; bar++) {
        // Half-time kick
        this._kickPattern.push(bar * 4);
        // Snare on 3
        this._snarePattern.push(bar * 4 + 2);
        // Hi-hat shuffles
        for (let beat = 0; beat < 4; beat++) {
          this._hihatPattern.push(bar * 4 + beat + 0.5);
          this._hihatPattern.push(bar * 4 + beat + 0.75);
        }
        // Bass wobble (root and octave)
        this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 1), dur: 1.5 });
        this._bassPattern.push({ time: bar * 4 + 1.5, note: scaleNote(this._key, this._scale, 7, 1), dur: 0.5 });
        this._bassPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 0, 1), dur: 1 });
        this._bassPattern.push({ time: bar * 4 + 3, note: scaleNote(this._key, this._scale, 3, 1), dur: 0.5 });
      }
    }
  }

  /* ── Trance ── */

  private buildTrancePatterns(section: string): void {
    const bars = this.totalBarsInSection;
    this._kickPattern = [];
    this._snarePattern = [];
    this._hihatPattern = [];
    this._bassPattern = [];
    this._leadPattern = [];
    this._padNotes = [];

    if (section === 'intro' || section === 'outro') {
      this.masterFilter.frequency.value = section === 'intro' ? 200 : 300;
      for (let bar = 0; bar < bars; bar++) {
        for (let beat = 0; beat < 4; beat++) {
          this._kickPattern.push(bar * 4 + beat);
        }
      }
    } else {
      this.masterFilter.frequency.value = 20000;
      for (let bar = 0; bar < bars; bar++) {
        for (let beat = 0; beat < 4; beat++) {
          this._kickPattern.push(bar * 4 + beat);
        }
        this._snarePattern.push(bar * 4 + 1);
        this._snarePattern.push(bar * 4 + 3);
        for (let beat = 0; beat < 4; beat++) {
          this._hihatPattern.push(bar * 4 + beat + 0.5);
        }
        // Off-beat bass (trance signature)
        this._bassPattern.push({ time: bar * 4 + 0.5, note: scaleNote(this._key, this._scale, 0, 2), dur: 0.5 });
        this._bassPattern.push({ time: bar * 4 + 1.5, note: scaleNote(this._key, this._scale, 0, 2), dur: 0.5 });
        this._bassPattern.push({ time: bar * 4 + 2.5, note: scaleNote(this._key, this._scale, 4, 2), dur: 0.5 });
        this._bassPattern.push({ time: bar * 4 + 3.5, note: scaleNote(this._key, this._scale, 4, 2), dur: 0.5 });
      }
      // Supersaw lead
      for (let bar = 0; bar < bars; bar += 2) {
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 5, 5), dur: 1 });
        this._leadPattern.push({ time: bar * 4 + 1, note: scaleNote(this._key, this._scale, 7, 5), dur: 0.5 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 8, 5), dur: 1 });
        this._leadPattern.push({ time: bar * 4 + 3, note: scaleNote(this._key, this._scale, 7, 5), dur: 0.5 });
      }
      // Pad
      this._padNotes = [
        scaleNote(this._key, this._scale, 0, 4),
        scaleNote(this._key, this._scale, 2, 4),
        scaleNote(this._key, this._scale, 4, 4),
      ];
    }
  }

  /* ── Hip-Hop ── */

  private buildHipHopPatterns(section: string): void {
    const bars = this.totalBarsInSection;
    this._kickPattern = [];
    this._snarePattern = [];
    this._hihatPattern = [];
    this._bassPattern = [];
    this._leadPattern = [];
    this._padNotes = [];

    if (section === 'intro' || section === 'outro') {
      this.masterFilter.frequency.value = section === 'intro' ? 200 : 300;
      for (let bar = 0; bar < bars; bar++) {
        this._kickPattern.push(bar * 4);
        this._snarePattern.push(bar * 4 + 2);
      }
    } else {
      this.masterFilter.frequency.value = 20000;
      for (let bar = 0; bar < bars; bar++) {
        // 808 kick
        this._kickPattern.push(bar * 4);
        if (bar % 2 === 0) this._kickPattern.push(bar * 4 + 2.5);
        // Snare on 2 and 4
        this._snarePattern.push(bar * 4 + 2);
        // Hi-hat rolls (triplet feel)
        for (let beat = 0; beat < 4; beat++) {
          if (beat === 1 || beat === 3) {
            this._hihatPattern.push(bar * 4 + beat);
            this._hihatPattern.push(bar * 4 + beat + 0.166);
            this._hihatPattern.push(bar * 4 + beat + 0.333);
          } else {
            this._hihatPattern.push(bar * 4 + beat + 0.5);
          }
        }
        // 808 sub-bass
        this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 2), dur: 3.5 });
      }
      // Melodic sample chops
      for (let bar = 0; bar < bars; bar += 2) {
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 4), dur: 0.5 });
        this._leadPattern.push({ time: bar * 4 + 1, note: scaleNote(this._key, this._scale, 3, 4), dur: 0.25 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 5, 4), dur: 0.5 });
      }
    }
  }

  /* ── Cinematic ── */

  private buildCinematicPatterns(section: string): void {
    const bars = this.totalBarsInSection;
    this._kickPattern = [];
    this._snarePattern = [];
    this._hihatPattern = [];
    this._bassPattern = [];
    this._leadPattern = [];
    this._padNotes = [];

    this.masterFilter.frequency.value = 20000;

    // Sparse percussion
    for (let bar = 0; bar < bars; bar++) {
      if (bar % 2 === 0) {
        this._kickPattern.push(bar * 4);
        this._kickPattern.push(bar * 4 + 2);
      }
    }

    // Epic pad swells
    this._padNotes = [
      scaleNote(this._key, this._scale, 0, 3),
      scaleNote(this._key, this._scale, 4, 3),
      scaleNote(this._key, this._scale, 7, 3),
      scaleNote(this._key, this._scale, 11, 3),
    ];

    // Deep sub-bass
    for (let bar = 0; bar < bars; bar++) {
      this._bassPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 0, 2), dur: 4 });
    }

    // Melodic theme
    if (section === 'drop' || section === 'build') {
      for (let bar = 0; bar < bars; bar += 2) {
        this._leadPattern.push({ time: bar * 4, note: scaleNote(this._key, this._scale, 5, 5), dur: 1.5 });
        this._leadPattern.push({ time: bar * 4 + 2, note: scaleNote(this._key, this._scale, 7, 5), dur: 1 });
        this._leadPattern.push({ time: bar * 4 + 3, note: scaleNote(this._key, this._scale, 8, 5), dur: 1 });
      }
    }
  }

  /* ─────── Scheduling Loop ─────── */

  private scheduleLoop(): void {
    if (!this._running) return;

    const sectionBars = this.totalBarsInSection;
    const totalBeats = sectionBars * 4;

    // Schedule all events for this section
    this.scheduleSectionEvents(totalBeats);

    // Schedule section advance
    this._loopId = Tone.getTransport().schedule((_time) => {
      if (!this._running) return;

      this._sectionBar++;
      void this.emitState();

      if (this._sectionBar >= sectionBars) {
        // Advance to next section
        this._sectionIndex = (this._sectionIndex + 1) % this._sectionCycle.length;
        this._sectionBar = 0;

        const sectionName = this.currentSectionName;
        const profile = GENRE_PROFILES[this._genre];
        const bpm = profile?.bpm ?? this._bpm;

        this.logDecision(`Section: ${sectionName} — ${this._genre} @ ${bpm}BPM`);
        this.buildPatterns();
        this.emitState();

        // Schedule the next section's events
        const nextBars = this.totalBarsInSection * 4;
        this.scheduleSectionEvents(nextBars);
      }
    }, totalBeats);
  }

  private scheduleSectionEvents(totalBeats: number): void {
    const now = Tone.now();

    // Schedule kick
    for (const beat of this._kickPattern) {
      if (beat >= totalBeats) continue;
      Tone.getTransport().schedule((time) => {
        if (!this._running) return;
        this.kick.triggerAttackRelease('C1', '8n', time);
      }, now + beat);
    }

    // Schedule snare
    for (const beat of this._snarePattern) {
      if (beat >= totalBeats) continue;
      Tone.getTransport().schedule((time) => {
        if (!this._running) return;
        this.snare.triggerAttackRelease('16n', time, 0.8);
      }, now + beat);
    }

    // Schedule hi-hat
    for (const beat of this._hihatPattern) {
      if (beat >= totalBeats) continue;
      Tone.getTransport().schedule((time) => {
        if (!this._running) return;
        this.hihat.triggerAttackRelease('32n', time, 0.3);
      }, now + beat);
    }

    // Schedule bass
    for (const event of this._bassPattern) {
      if (event.time >= totalBeats) continue;
      Tone.getTransport().schedule((time) => {
        if (!this._running) return;
        this.bass.triggerAttackRelease(event.note, event.dur, time);
      }, now + event.time);
    }

    // Schedule lead
    for (const event of this._leadPattern) {
      if (event.time >= totalBeats) continue;
      Tone.getTransport().schedule((time) => {
        if (!this._running) return;
        this.lead.triggerAttackRelease(event.note, event.dur, time);
      }, now + event.time);
    }

    // Schedule pad (sustained chord across section)
    if (this._padNotes.length > 0) {
      Tone.getTransport().schedule((time) => {
        if (!this._running) return;
        this._padNotes.forEach(note => {
          this.pad.triggerAttackRelease(note, totalBeats * 0.5, time);
        });
      }, now);
    }
  }

  /* ─────── Utility ─────── */

  private logDecision(msg: string): void {
    const prefix = '[Composer]';
    console.log(`${prefix} ${msg}`);
    this.onDecision?.(msg);
  }

  private emitState(): ComposerState {
    const st = this.getState();
    this.onStateChange?.(st);
    return st;
  }

  /** Dispose all resources. */
  dispose(): void {
    this.stop();
    this.kick.dispose();
    this.snare.dispose();
    this.hihat.dispose();
    this.bass.dispose();
    this.lead.dispose();
    this.pad.dispose();
    this.bassFilter.dispose();
    this.masterFilter.dispose();
    this.masterGain.dispose();
    console.log('[Composer] Engine disposed');
  }
}